import type { JSONContent } from "@tiptap/core"
import type { MentionItem } from "@/components/prompt-editor/types"

const CHAT_TOKEN_RE = /@node\[(\d+)\]|@img_(\d+)|@vid_(\d+)/g

export type AgentMentionAttachment = {
  previewUrl?: string | null
  mediaType?: "image" | "video"
}

export function agentImageMentionToken(index: number, mediaType: "image" | "video" = "image"): string {
  return mediaType === "video" ? `@vid_${index}` : `@img_${index}`
}

function mentionAttachment(value: string | null | undefined | AgentMentionAttachment): AgentMentionAttachment {
  if (value != null && typeof value === "object") return value
  return { previewUrl: value, mediaType: "image" }
}

/** 1-based tokens in attachment order. A video at position 2 is `@vid_2`, not a second image counter. */
export function agentImageMentionItems(
  attachments: readonly (string | null | undefined | AgentMentionAttachment)[],
): MentionItem[] {
  return attachments.map((value, offset) => {
    const attachment = mentionAttachment(value)
    const id = offset + 1
    const mediaType = attachment.mediaType === "video" ? "video" : "image"
    const video = mediaType === "video"
    return {
      category: video ? "vid" : "img",
      id,
      label: agentImageMentionToken(id, mediaType),
      imageUrl: video ? null : attachment.previewUrl || null,
      mediaType,
    }
  })
}

/**
 * Drop `@img_N` / `@vid_N` for the removed 1-based index and shift later tokens down.
 * The prefix stays with the attachment; only the position number moves.
 * Collapses the horizontal space the token left behind.
 */
export function remapAgentImageTokens(text: string, removedIndex: number): string {
  if (!Number.isInteger(removedIndex) || removedIndex < 1) return text
  const removedRe = new RegExp(`@(?:img|vid)_${removedIndex}(?!\\d)`)
  const shift = (value: string) =>
    value.replace(/@(img|vid)_(\d+)/g, (token, kind: string, raw: string) => {
      const index = Number(raw)
      if (index > removedIndex) return `@${kind}_${index - 1}`
      return token
    })
  if (!removedRe.test(text)) return shift(text)

  const marked = text.replace(new RegExp(`@(?:img|vid)_${removedIndex}(?!\\d)`, "g"), "\uE000")
  return shift(marked)
    .replace(/[^\S\n]*\uE000[^\S\n]*([,.;:!?])/g, "$1")
    .replace(/[^\S\n]*\uE000[^\S\n]*/g, " ")
    .replace(/^[^\S\n]+|[^\S\n]+$/g, "")
}

export type AgentChatMentionPart =
  | { type: "text"; text: string }
  | {
      type: "mention"
      category: "node" | "img" | "vid"
      id: number
      label: string
      imageUrl: string | null
      mediaType?: MentionItem["mediaType"]
    }

/** Один проход: текст и чипы `@node[id]` / `@img_N` / `@vid_N`. Поле ввода и история собирают вид отсюда. */
function walkChatTokens(text: string, items: readonly MentionItem[]): AgentChatMentionPart[] {
  const nodes = new Map(items.filter((item) => item.category === "node").map((item) => [item.id, item]))
  const images = new Map(items.filter((item) => item.category === "img").map((item) => [item.id, item]))
  const videos = new Map(items.filter((item) => item.category === "vid").map((item) => [item.id, item]))
  const parts: AgentChatMentionPart[] = []
  let last = 0
  for (const match of text.matchAll(CHAT_TOKEN_RE)) {
    const index = match.index ?? 0
    if (index > last) parts.push({ type: "text", text: text.slice(last, index) })
    if (match[1] != null) {
      const id = Number(match[1])
      const item = nodes.get(id)
      parts.push({
        type: "mention",
        category: "node",
        id,
        label: item?.label ?? match[0],
        imageUrl: item?.imageUrl ?? null,
        ...(item?.mediaType ? { mediaType: item.mediaType } : {}),
      })
    } else if (match[3] != null) {
      const id = Number(match[3])
      const item = videos.get(id)
      parts.push({
        type: "mention",
        category: "vid",
        id,
        label: item?.label ?? agentImageMentionToken(id, "video"),
        imageUrl: null,
        mediaType: "video",
      })
    } else {
      const id = Number(match[2])
      const item = images.get(id)
      parts.push({
        type: "mention",
        category: "img",
        id,
        label: item?.label ?? agentImageMentionToken(id),
        imageUrl: item?.imageUrl ?? null,
        mediaType: "image",
      })
    }
    last = index + match[0].length
  }
  if (last < text.length || parts.length === 0) parts.push({ type: "text", text: text.slice(last) })
  return parts
}

/** Split a stored chat message into text, `@node[id]` chips, and `@img_N` / `@vid_N` chips. */
export function splitAgentChatMentions(text: string, items: readonly MentionItem[]): AgentChatMentionPart[] {
  return walkChatTokens(text, items)
}

/** Composer document: `@img_N`, `@vid_N`, and `@node[id]` become mention chips. */
export function agentComposerTextToDoc(text: string, items: readonly MentionItem[]): JSONContent {
  if (text === "") return { type: "doc", content: [{ type: "paragraph" }] }
  return {
    type: "doc",
    content: text.split("\n").map((line) => {
      if (line.length === 0) return { type: "paragraph" }
      const content: JSONContent[] = walkChatTokens(line, items).flatMap((part): JSONContent[] => {
        if (part.type === "text") {
          return part.text.length === 0 ? [] : [{ type: "text" as const, text: part.text }]
        }
        return [
          {
            type: "mention" as const,
            attrs: { category: part.category, id: part.id, label: part.label },
          },
        ]
      })
      return content.length === 0 ? { type: "paragraph" } : { type: "paragraph", content }
    }),
  }
}
